import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { useCachedData } from '../services/cacheService';
import { usePreloadPage } from '../services/preloadService';
import { useMobileOptimizations } from '../hooks/useMobileOptimizations';
import {
  LayoutDashboard,
  Package,
  Users,
  ShoppingCart,
  Settings,
  LogOut,
  Sun,
  Moon,
  Menu,
  X,
  ChevronLeft,
  BarChart3,
  TrendingUp,
  Tag,
  Eye,
  Truck,
  Facebook,
  Clock,
  Table,
  CreditCard,
  Monitor,
  FileText,
  ShieldCheck
} from 'lucide-react';
import DashboardHome from '../components/dashboard/DashboardHome';
import ProdutosPage from '../components/dashboard/ProdutosPage';
import CategoriasPage from '../components/dashboard/CategoriasPage';
import PedidosPage from '../components/dashboard/PedidosPage';
import UsuariosPage from '../components/dashboard/UsuariosPage';
import ConfiguracoesPage from '../components/dashboard/ConfiguracoesPage';
import RelatoriosPage from '../components/dashboard/RelatoriosPage';
import EstoquePage from '../components/dashboard/EstoquePage';
import PromocoesPage from '../components/dashboard/PromocoesPage';
import FretePage from '../components/dashboard/FretePage';
import MetaPixelPage from '../components/dashboard/MetaPixelPage';
import HorarioFuncionamentoPage from '../components/dashboard/HorarioFuncionamentoPage';
import MesasPage from '../components/dashboard/MesasPage';
import ComandasFisicasPage from '../components/dashboard/ComandasFisicasPage';
import EnvConfigPage from '../components/dashboard/EnvConfigPage';
import AdministradoresPage from '../components/dashboard/AdministradoresPage';

const Dashboard: React.FC = () => {
  const { usuario, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, isTablet } = useMobileOptimizations();

  // Estado para tipo de dispositivo
  const [deviceType, setDeviceType] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  const [screenWidth, setScreenWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 0);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Desktop: pode ser colapsado, Mobile/Tablet: sempre fechado
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [darkMode, setDarkMode] = useState(true);
  const [empresa, setEmpresa] = useState<any>(null);

  // Detectar tipo de dispositivo
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const width = window.innerWidth;
    const isIPadWidth = width >= 768 && width <= 1366;
    const isIPadUserAgent = /iPad|Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 0;
    const isIPadDevice = isIPadWidth || isIPadUserAgent;

    let newDeviceType: 'mobile' | 'tablet' | 'desktop';

    if (isMobile && !isIPadDevice) {
      newDeviceType = 'mobile';
    } else if (isTablet || isIPadDevice) {
      newDeviceType = 'tablet';
    } else {
      newDeviceType = 'desktop';
    }

    setDeviceType(newDeviceType);
    setScreenWidth(width);

    // Em mobile/tablet, sempre fechar sidebar ao detectar mudança
    if (newDeviceType !== 'desktop') {
      setSidebarOpen(false);
      setSidebarCollapsed(false);
    }
  }, [isMobile, isTablet]);

  // Atualizar largura da tela
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Forçar tema dark no carregamento
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Usar cache para dados da empresa
  const { data: empresaData } = useCachedData(
    'empresa_1',
    async () => {
      const response = await api.get('/api/empresas/1');
      return response.data;
    },
    10 * 60 * 1000, // 10 minutos
    []
  );

  // Pré-carregar páginas do dashboard
  usePreloadPage('dashboard');

  useEffect(() => {
    if (empresaData) {
      setEmpresa(empresaData);
    } else {
      // Fallback para dados padrão
      setEmpresa({
        nome: 'DK System',
        endereco: 'Endereço não informado',
        telefone: '(00) 00000-0000'
      });
    }
  }, [empresaData]);

  useEffect(() => {
    // Só salvar estado de colapso no desktop
    if (deviceType === 'desktop') {
      localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
    }
  }, [sidebarCollapsed, deviceType]);

  // Bloquear scroll do body quando sidebar aberto no mobile
  useEffect(() => {
    if (deviceType === 'mobile' && sidebarOpen) {
      // Salvar posição atual do scroll
      const scrollY = window.scrollY;

      // Aplicar estilos para bloquear scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      // Cleanup: restaurar scroll quando sidebar fechar
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [deviceType, sidebarOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Produtos', href: '/dashboard/produtos', icon: Package },
    { name: 'Categorias', href: '/dashboard/categorias', icon: Package },
    { name: 'Pedidos Delivery', href: '/dashboard/pedidos', icon: ShoppingCart },
    { name: 'Estoque', href: '/dashboard/estoque', icon: TrendingUp },
    { name: 'Promoções', href: '/dashboard/promocoes', icon: Tag },
    { name: 'Frete', href: '/dashboard/frete', icon: Truck },
    { name: 'Meta Pixel', href: '/dashboard/meta-pixel', icon: Facebook },
    { name: 'Horário de Funcionamento', href: '/dashboard/horario-funcionamento', icon: Clock },
    { name: 'Mesas', href: '/dashboard/mesas', icon: Table },
    { name: 'Comandas Físicas', href: '/dashboard/comandas-fisicas', icon: CreditCard },
    ...((usuario?.role === 'gestao' || usuario?.nivel === 'admin' || usuario?.nivel === 'gerente') ? [
      { name: 'Administradores', href: '/dashboard/administradores', icon: ShieldCheck },
      { name: 'Usuários', href: '/dashboard/usuarios', icon: Users },
      { name: 'Relatórios', href: '/dashboard/relatorios', icon: BarChart3 },
      { name: 'Config. Ambiente', href: '/dashboard/env-config', icon: FileText },
    ] : []),
    { name: 'Configurações', href: '/dashboard/configuracoes', icon: Settings },
  ];

  const handleVisaoCliente = () => {
    const empresaId = usuario?.empresa_id;
    if (empresaId) {
      window.open(`/menu/${empresaId}`, '_blank');
    }
  };

  const handleVisaoGarcom = () => {
    window.open('/garcom/login', '_blank');
  };

  const handleVisaoPDV = () => {
    const hostname = window.location.hostname;
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0';
    const pdvURL = isLocal ? 'http://localhost:5174' : `${window.location.origin}/pdv/`;
    window.open(pdvURL, '_blank');
  };

  const isActive = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard';
    } else {
      return location.pathname === href || location.pathname.startsWith(href + '/');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Sidebar Mobile - Apenas para mobile */}
      {deviceType === 'mobile' && (
        <div className={`fixed inset-0 z-50 ${sidebarOpen ? 'block' : 'hidden'}`}>
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity cursor-pointer"
            onClick={() => setSidebarOpen(false)}
            style={{ pointerEvents: 'auto' }}
          />
          <div className="fixed inset-y-0 left-0 flex w-72 flex-col bg-white dark:bg-gray-800 shadow-xl transform transition-transform">
            <div className="flex h-14 items-center justify-between px-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-base font-semibold text-gray-700 dark:text-gray-300">Menu Dashboard</h2>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 space-y-1 px-3 py-4 overflow-y-auto">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`group flex w-full items-center justify-start text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${isActive(item.href)
                      ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                      }`}
                  >
                    <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                    <span className="truncate">{item.name}</span>
                  </a>
                );
              })}
            </nav>

            {/* Botões de Visão - Mobile */}
            <div className="px-3 py-4 border-t border-gray-200 dark:border-gray-700 space-y-2 bg-gray-50 dark:bg-gray-900/50">
              <button
                onClick={handleVisaoCliente}
                className="group flex items-center justify-start text-left w-full px-3 py-2.5 text-sm font-medium rounded-lg text-green-600 hover:bg-green-50 hover:text-green-700 dark:text-green-400 dark:hover:bg-green-900/20 dark:hover:text-green-300 transition-colors"
              >
                <Eye className="mr-3 h-5 w-5 flex-shrink-0" />
                <span>Visão do Cliente</span>
              </button>
              <button
                onClick={handleVisaoGarcom}
                className="group flex items-center justify-start text-left w-full px-3 py-2.5 text-sm font-medium rounded-lg text-blue-600 hover:bg-blue-50 hover:text-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-300 transition-colors"
              >
                <Users className="mr-3 h-5 w-5 flex-shrink-0" />
                <span>Visão do Garçom</span>
              </button>
              <button
                onClick={handleVisaoPDV}
                className="group flex items-center justify-start text-left w-full px-3 py-2.5 text-sm font-medium rounded-lg text-purple-600 hover:bg-purple-50 hover:text-purple-700 dark:text-purple-400 dark:hover:bg-purple-900/20 dark:hover:text-purple-300 transition-colors"
              >
                <Monitor className="mr-3 h-5 w-5 flex-shrink-0" />
                <span>Sistema PDV</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar Tablet - Apenas para tablet/iPad */}
      {deviceType === 'tablet' && (
        <div className={`fixed inset-0 z-50 ${sidebarOpen ? 'block' : 'hidden'}`}>
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 flex w-80 flex-col bg-white dark:bg-gray-800 shadow-xl transform transition-transform">
            <div className="flex h-16 items-center justify-between px-5 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300">Menu Dashboard</h2>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex-1 space-y-1 px-4 py-5 overflow-y-auto">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`group flex w-full items-center justify-start text-left px-4 py-3 text-base font-medium rounded-lg transition-colors ${isActive(item.href)
                      ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                      }`}
                  >
                    <Icon className="mr-4 h-5 w-5 flex-shrink-0" />
                    <span className="truncate">{item.name}</span>
                  </a>
                );
              })}
            </nav>

            {/* Botões de Visão - Tablet */}
            <div className="px-4 py-5 border-t border-gray-200 dark:border-gray-700 space-y-2.5 bg-gray-50 dark:bg-gray-900/50">
              <button
                onClick={handleVisaoCliente}
                className="group flex items-center justify-start text-left w-full px-4 py-3 text-base font-medium rounded-lg text-green-600 hover:bg-green-50 hover:text-green-700 dark:text-green-400 dark:hover:bg-green-900/20 dark:hover:text-green-300 transition-colors"
              >
                <Eye className="mr-4 h-5 w-5 flex-shrink-0" />
                <span>Visão do Cliente</span>
              </button>
              <button
                onClick={handleVisaoGarcom}
                className="group flex items-center justify-start text-left w-full px-4 py-3 text-base font-medium rounded-lg text-blue-600 hover:bg-blue-50 hover:text-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-300 transition-colors"
              >
                <Users className="mr-4 h-5 w-5 flex-shrink-0" />
                <span>Visão do Garçom</span>
              </button>
              <button
                onClick={handleVisaoPDV}
                className="group flex items-center justify-start text-left w-full px-4 py-3 text-base font-medium rounded-lg text-purple-600 hover:bg-purple-50 hover:text-purple-700 dark:text-purple-400 dark:hover:bg-purple-900/20 dark:hover:text-purple-300 transition-colors"
              >
                <Monitor className="mr-4 h-5 w-5 flex-shrink-0" />
                <span>Sistema PDV</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar Desktop - Apenas para desktop */}
      {deviceType === 'desktop' && (
        <div className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col transition-all duration-300 ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'}`}>
          <div className="flex flex-col flex-grow bg-white dark:bg-gray-800 pt-5 pb-4 overflow-y-auto border-r border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between flex-shrink-0 px-4">
              {!sidebarCollapsed && (
                <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300">Menu Dashboard</h2>
              )}
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                {sidebarCollapsed ? <Menu className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              </button>
            </div>
            <nav className="mt-8 flex-1 px-2 space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    className={`group flex w-full items-center justify-start text-left px-2 py-2 text-sm font-medium rounded-md transition-colors ${isActive(item.href)
                      ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                      }`}
                    title={sidebarCollapsed ? item.name : undefined}
                  >
                    <Icon className={`h-5 w-5 flex-shrink-0 ${sidebarCollapsed ? '' : 'mr-3'}`} />
                    {!sidebarCollapsed && <span className="truncate">{item.name}</span>}
                  </a>
                );
              })}
            </nav>

            {/* Botões de Visão - Desktop */}
            <div className="px-2 py-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
              <button
                onClick={handleVisaoCliente}
                className="group flex items-center justify-start text-left w-full px-2 py-2 text-sm font-medium rounded-md text-green-600 hover:bg-green-50 hover:text-green-700 dark:text-green-400 dark:hover:bg-green-900/20 dark:hover:text-green-300 transition-colors"
                title={sidebarCollapsed ? 'Visão do Cliente' : undefined}
              >
                <Eye className={`h-5 w-5 flex-shrink-0 ${sidebarCollapsed ? '' : 'mr-3'}`} />
                {!sidebarCollapsed && <span className="truncate">Visão do Cliente</span>}
              </button>
              <button
                onClick={handleVisaoGarcom}
                className="group flex items-center justify-start text-left w-full px-2 py-2 text-sm font-medium rounded-md text-blue-600 hover:bg-blue-50 hover:text-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-300 transition-colors"
                title={sidebarCollapsed ? 'Visão do Garçom' : undefined}
              >
                <Users className={`h-5 w-5 flex-shrink-0 ${sidebarCollapsed ? '' : 'mr-3'}`} />
                {!sidebarCollapsed && <span className="truncate">Visão do Garçom</span>}
              </button>
              <button
                onClick={handleVisaoPDV}
                className="group flex items-center justify-start text-left w-full px-2 py-2 text-sm font-medium rounded-md text-purple-600 hover:bg-purple-50 hover:text-purple-700 dark:text-purple-400 dark:hover:bg-purple-900/20 dark:hover:text-purple-300 transition-colors"
                title={sidebarCollapsed ? 'Sistema PDV' : undefined}
              >
                <Monitor className={`h-5 w-5 flex-shrink-0 ${sidebarCollapsed ? '' : 'mr-3'}`} />
                {!sidebarCollapsed && <span className="truncate">Sistema PDV</span>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main content - Layout isolado por dispositivo */}
      <div className={`
        ${deviceType === 'mobile' ? 'w-full' : ''}
        ${deviceType === 'tablet' ? 'w-full' : ''}
        ${deviceType === 'desktop' ? `lg:flex lg:flex-col lg:flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}` : ''}
      `} style={deviceType === 'mobile' && sidebarOpen ? { pointerEvents: 'none' } : {}}>
        {/* Top bar - Isolado por dispositivo */}
        <div className={`
          sticky top-0 z-40 flex shrink-0 items-center border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm
          ${deviceType === 'mobile' ? 'h-14 px-3 gap-x-2' : ''}
          ${deviceType === 'tablet' ? 'h-16 px-4 gap-x-3' : ''}
          ${deviceType === 'desktop' ? 'h-16 px-4 sm:px-6 lg:px-8 gap-x-4 sm:gap-x-6' : ''}
        `}>
          {/* Botão menu mobile/tablet */}
          {(deviceType === 'mobile' || deviceType === 'tablet') && (
            <button
              type="button"
              className={`p-2 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${deviceType === 'mobile' ? 'p-2' : 'p-2.5'
                }`}
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className={deviceType === 'mobile' ? 'h-5 w-5' : 'h-6 w-6'} />
            </button>
          )}

          {/* Título - Responsivo por dispositivo */}
          <div className="flex-1 flex justify-center">
            <h1 className={`
              font-black bg-gradient-to-r from-orange-400 via-red-500 to-orange-600 bg-clip-text text-transparent drop-shadow-lg tracking-wider font-serif transform transition-all duration-500 hover:scale-105 cursor-default select-none
              ${deviceType === 'mobile' ? 'text-lg' : ''}
              ${deviceType === 'tablet' ? 'text-2xl' : ''}
              ${deviceType === 'desktop' ? 'text-3xl hover:rotate-1 hover:drop-shadow-2xl' : ''}
            `}>
              {empresa?.nome || 'DK System'}
            </h1>
          </div>

          {/* Controles do usuário - Responsivo por dispositivo */}
          <div className={`
            flex items-center
            ${deviceType === 'mobile' ? 'gap-x-2' : ''}
            ${deviceType === 'tablet' ? 'gap-x-3' : ''}
            ${deviceType === 'desktop' ? 'gap-x-4 lg:gap-x-6' : ''}
          `}>
            {/* Theme toggle */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`
                text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors
                ${deviceType === 'mobile' ? 'p-1.5' : 'p-2'}
              `}
            >
              {darkMode ? (
                <Sun className={deviceType === 'mobile' ? 'h-4 w-4' : deviceType === 'tablet' ? 'h-5 w-5' : 'h-5 w-5'} />
              ) : (
                <Moon className={deviceType === 'mobile' ? 'h-4 w-4' : deviceType === 'tablet' ? 'h-5 w-5' : 'h-5 w-5'} />
              )}
            </button>

            {/* User menu */}
            <div className={`
              flex items-center
              ${deviceType === 'mobile' ? 'gap-x-2' : ''}
              ${deviceType === 'tablet' ? 'gap-x-3' : ''}
              ${deviceType === 'desktop' ? 'gap-x-4' : ''}
            `}>
              {/* Informações do usuário - Oculto no mobile */}
              {deviceType !== 'mobile' && (
                <div className="flex flex-col items-end">
                  <p className={`
                    font-medium text-gray-900 dark:text-white
                    ${deviceType === 'tablet' ? 'text-sm' : 'text-sm'}
                    ${deviceType === 'desktop' ? 'sm:block' : ''}
                  `}>
                    {usuario?.nome}
                  </p>
                  <p className={`
                    text-gray-500 dark:text-gray-400 capitalize
                    ${deviceType === 'tablet' ? 'text-xs' : 'text-xs'}
                    ${deviceType === 'desktop' ? 'sm:block' : ''}
                  `}>
                    {usuario?.role}
                  </p>
                </div>
              )}
              <button
                onClick={handleLogout}
                className={`
                  text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors
                  ${deviceType === 'mobile' ? 'p-1.5' : 'p-2'}
                `}
              >
                <LogOut className={deviceType === 'mobile' ? 'h-4 w-4' : deviceType === 'tablet' ? 'h-5 w-5' : 'h-5 w-5'} />
              </button>
            </div>
          </div>
        </div>

        {/* Page content - Padding isolado por dispositivo */}
        <main className="flex-1">
          <div className={`
            ${deviceType === 'mobile' ? 'py-3' : ''}
            ${deviceType === 'tablet' ? 'py-4' : ''}
            ${deviceType === 'desktop' ? 'py-6' : ''}
          `}>
            <div className={`
              mx-auto max-w-7xl
              ${deviceType === 'mobile' ? 'px-3' : ''}
              ${deviceType === 'tablet' ? 'px-4' : ''}
              ${deviceType === 'desktop' ? 'px-4 sm:px-6 lg:px-8' : ''}
            `}>
              <Routes>
                <Route path="/" element={<DashboardHome />} />
                <Route path="/produtos" element={<ProdutosPage />} />
                <Route path="/categorias" element={<CategoriasPage />} />
                <Route path="/pedidos" element={<PedidosPage />} />
                <Route path="/estoque" element={<EstoquePage />} />
                <Route path="/promocoes" element={<PromocoesPage />} />
                <Route path="/frete" element={<FretePage />} />
                <Route path="/meta-pixel" element={<MetaPixelPage />} />
                <Route path="/horario-funcionamento" element={<HorarioFuncionamentoPage />} />
                <Route path="/mesas" element={<MesasPage />} />
                <Route path="/comandas-fisicas" element={<ComandasFisicasPage />} />
                {(usuario?.role === 'gestao' || usuario?.nivel === 'admin' || usuario?.nivel === 'gerente') && (
                  <>
                    <Route path="/administradores" element={<AdministradoresPage />} />
                    <Route path="/usuarios" element={<UsuariosPage />} />
                    <Route path="/relatorios" element={<RelatoriosPage />} />
                  </>
                )}
                <Route path="/configuracoes" element={<ConfiguracoesPage />} />
                {(usuario?.role === 'gestao' || usuario?.nivel === 'admin' || usuario?.nivel === 'gerente') && (
                  <Route path="/env-config" element={<EnvConfigPage />} />
                )}
              </Routes>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
